import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-oneportal";
import dashboardWorkspaceOnboardingVideo from "@/assets/videos/dashboard-workspace-onboarding-web-FPO.mp4";
import styles from "./OnboardingTour.module.css";

export interface OnboardingTourProps {
  onBack: () => void;
  onFinish: () => void;
  onSkip: () => void;
  completesOnLastStep?: boolean;
}

interface TourStepContent {
  titleKey: string;
  descriptionKey: string;
  videoSrc: string;
}

const TOUR_STEPS: TourStepContent[] = [
  {
    titleKey: "onboardingTour.step1.title",
    descriptionKey: "onboardingTour.step1.description",
    videoSrc: dashboardWorkspaceOnboardingVideo,
  },
  {
    titleKey: "onboardingTour.step2.title",
    descriptionKey: "onboardingTour.step2.description",
    videoSrc: dashboardWorkspaceOnboardingVideo,
  },
  {
    titleKey: "onboardingTour.step3.title",
    descriptionKey: "onboardingTour.step3.description",
    videoSrc: dashboardWorkspaceOnboardingVideo,
  },
];

/**
 * Multi-step image carousel shown after the welcome screen. Stepping back
 * from the first slide hands control back to the caller (the welcome screen).
 */
export const OnboardingTour: React.FC<OnboardingTourProps> = ({
  onBack,
  onFinish,
  onSkip,
  completesOnLastStep = false,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const step = TOUR_STEPS[stepIndex];
  const isLastStep = stepIndex === TOUR_STEPS.length - 1;

  const handleBack = () => {
    if (stepIndex === 0) {
      onBack();
      return;
    }
    setStepIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isLastStep) {
      onFinish();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-tour-title"
      data-testid="onboarding-tour"
    >
      <div className={styles.content}>
        <div className={styles.skipRow}>
          <button
            type="button"
            className={styles.skipButton}
            onClick={onSkip}
            data-testid="onboarding-tour-skip"
          >
              <span>{t("onboardingTour.skip")}</span>{" "}
              <span aria-hidden="true">&gt;</span>
          </button>
        </div>
        <div className={styles.screenshotFrame}>
          <video
            key={stepIndex}
            src={step.videoSrc}
            className={styles.screenshot}
            autoPlay
            loop
            muted
            playsInline
            aria-label={t(step.titleKey)}
            data-testid="onboarding-tour-video"
          />
        </div>
        <h2 id="onboarding-tour-title" className={styles.title}>
          {t(step.titleKey)}
        </h2>
        <p className={styles.description}>
          {t(step.descriptionKey)}
        </p>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="highlightTertiary"
            className={styles.backButton}
            onClick={handleBack}
            data-testid="onboarding-tour-back"
          >
            {t("common.back")}
          </Button>
          <Button
            type="button"
            variant="primary"
            autoFocus
            className={styles.nextButton}
            onClick={handleNext}
            data-testid="onboarding-tour-next"
          >
            {isLastStep && completesOnLastStep
              ? t("onboardingTour.locationStep.finish")
              : t("common.next")}
          </Button>
        </div>
        <div className={styles.dots} data-testid="onboarding-tour-dots">
          {TOUR_STEPS.map((_, index) => (
            <span
              key={index}
              className={`${styles.dot} ${
                index === stepIndex ? styles.dotActive : ""
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
